import { type DetectTextResult } from '@air/feature-ag-ui-chat';

import { type LocalizationElement, type LocalizationImageDimensions } from './localizationTypes';

const toElementId = (index: number): string => `region-${index}`;

/** Drops OCR noise — empty regions and symbol-only fragments (punctuation ticks, dividers). */
export const isTranslatableOcrText = (text: string): boolean => {
  const trimmed = text.trim();

  if (!trimmed) {
    return false;
  }

  // Require at least one letter or number so "-", "=", "|", etc. are ignored.
  return /[\p{L}\p{N}]/u.test(trimmed);
};

/**
 * Maps a `detect_text` OCR result into translatable draft elements. Translations start empty —
 * `useLocalizationTranslate` fills them in per span once a target language is committed, unless
 * the user edits a row first.
 */
export const buildDetectedElementsFromOcr = (
  result: DetectTextResult,
): { elements: LocalizationElement[]; imageDimensions: LocalizationImageDimensions } => {
  const elements = result.regions
    .filter((region) => isTranslatableOcrText(region.text))
    .map(
      (region, index): LocalizationElement => ({
        boundingBox: region.boundingBox,
        fetchedTranslations: {},
        id: toElementId(index),
        originalText: region.text.trim(),
        translationOrigins: {},
        translations: {},
        words: region.words ?? [],
      }),
    );

  return {
    elements,
    imageDimensions: result.imageDimensions,
  };
};
